import moment from "moment";

import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { Recall } from "@/Common/Api/Communications/Dto/Recall";
import { Address as MyleModelsCommonResponseAddress } from "@/Common/Api/Contacts/Dto/Address";
import { ContactPoint } from "@/Common/Api/Contacts/Dto/ContactPoint";
import { TransferablePatientInfoResponse } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { Inactivable } from "@/Common/Api/Individuals/Dto/Inactivable";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { ClinicalWaitingList } from "@/Common/Api/Lists/Dto/ClinicalWaitingList";
import { IConsentProgramHistory } from "@/Common/Api/Patient/Dto/IConsentProgramHistory";
import { InsuranceDetail } from "@/Common/Api/Patient/Dto/InsuranceDetail";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { LoadPatientModel } from "@/Common/Api/Patient/Dto/LoadPatientModel";
import { Patient as ResponsePatient } from "@/Common/Api/Patient/Dto/Patient";
import { ExternalPatientSubscriptionInfo } from "@/Common/Api/Patient/Dto/PatientSubscriptions/ExternalPatientSubscriptionInfo";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { HealthCardHelper } from "@/Common/Helpers/HealthCareHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Address as MyleModelsCommonAddress } from "@/Common/Models/Contacts/Address";
import { Email } from "@/Common/Models/Contacts/Email";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { PhoneNumber, PhoneNumberDeprecated } from "@/Common/Models/Contacts/PhoneNumber";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { Ethnicities } from "@/Common/Models/Individuals/Enums/Ethnicity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { CommonStatus } from "@/Common/Models/Patient/Enum/CommonStatus";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { Gender, PossiblePatientWarning } from "@/Common/Models/Patient/PatientEnums";
import { PatientExternalIdentifier as PatientExternalIdentifier } from "@/Common/Models/Patient/PatientExternalIdentifier";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { IPatientContactsModel } from "@/Common/Models/Patient/UI/Profile/IPatientContactsModel";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { IPatientEnrollmentModel } from "@/Common/Models/Patient/UI/Profile/IPatientEnrollmentModel";
import { IPatientIdentifiersModel } from "@/Common/Models/Patient/UI/Profile/IPatientIdentifiersModel";
import { IPatientRelationshipsModel } from "@/Common/Models/Patient/UI/Profile/IPatientRelationshipsModel";
import { ReligionCache } from "@/Common/Models/Patient/UI/Profile/ReligionModel";
import { BasePerson } from "@/Common/Models/Person/BasePerson";
import { PatientPopulationBasedFundingModel } from "@/Common/Models/PopulationBasedFunding/PatientPopulationBasedFundingModel";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { RAMQInfo, RamqStatus } from "@/Common/Models/RAMQ/RAMQInfo";
import { RAMQInscription } from "@/Common/Models/RAMQ/RAMQInscription";
import { ProfilePicture as MyleModelsCommonProfilePicture } from "@/Common/Models/UI/ProfilePicture";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { Guid } from "../Guid";
import { EmployeeLight } from "../UI/Employee/EmployeeLight";
import { AllowlistType } from "../UI/Enums";
import { IPatientOtherInformationModel } from "./UI/Profile/IPatientOtherInformationModel";
import { IPatientFileAllowlist } from "./IPatientFileAllowlist";

class Patient extends BasePerson {
    // Base Info
    public IndividualResponse: Individual;
    public PatientResponse: ResponsePatient;
    public InactivableResponse: Inactivable;

    public ExternalIdentifiers: PatientExternalIdentifier[];

    public ConsentProgramHistory: IConsentProgramHistory[];

    public ProfilePicture: MyleModelsCommonProfilePicture;

    public IntervenorsOnFile: PatientRefIntervenorWithDetailsModel[];

    // Other info
    public PreferredPharmacy: PharmacyProxy;
    public InsuranceCompany: InsuranceDetail;
    public ExternalPatientSubscriptionInfo: ExternalPatientSubscriptionInfo[];

    // Related Information / Alerts
    public UpcomingAppointment: UpcomingAppointment;
    public RelatedRecalls: Recall[];
    public ClinicalWaitingLists: ClinicalWaitingList[];

    public RAMQInfo: RAMQInfo; // set separately
    public OntarioPatientEnrollmentModel: OntarioPatientEnrollmentModel;

    public PopulationBasedFunding: PatientPopulationBasedFundingModel;

    // Missing patient info required for basic functionality
    public possiblePatientWarning: PossiblePatientWarning[];

    public IsReadOnly?: boolean;
    public PatientFileAllowlist: IPatientFileAllowlist;

    public constructor(init?: Partial<Patient>) {
        super();
        Object.assign(this, init);

        if (!cacheStore.ClinicSettings.EnablePopulationBasedFunding)
            delete this.PopulationBasedFunding;
    }

    public get RAMQInscription(): RAMQInscription {
        return this.RAMQInfo && this.RAMQInfo.RAMQInscription;
    }

    /**
     * Set the patient's RAMQ info.
     * @param info The RAMQ info to set.
     */
    public setRamq(info: RAMQInfo): void {
        this.RAMQInfo = info;
    }

    public get HasRamqResponse(): boolean {
        return this.RAMQInfo != null;
    }

    public static FromLoadAppointment(app: Appointment): Patient {
        return new Patient({
            IndividualResponse: app.PatientIndividual,
            PatientResponse: app.Patient,
            HealthCards: app.PatientHealthCards,
            OtherPatientIdentifiers: app.PatientOtherPatientIdentifiers,
            Addresses: app.PatientAddresses,
            Phones: app.PatientPhoneNumbers,
            Emails: app.PatientEmails,
            ProfilePicture: app.PatientProfilePicure,
            UpcomingAppointment: app.PatientUpcomingAppointment,
            RelatedRecalls: app.PatientRelatedRecalls,
            ClinicalWaitingLists: app.PatientClinicalWaitingLists,
            Relationships: app.PatientRelationships,
            IntervenorsOnFile: app.PatientIntervenorsOnFile?.map(x => new PatientRefIntervenorWithDetailsModel(x))
        });
    }

    public static FromResponse(response: ServerEvent): Patient {
        return this.FromResponseData(response.Data);
    }

    public static fromResponseData(response: LoadPatientModel): Patient {
        return this.FromResponseData(response);
    }
    public static FromLinkedContact(linkedContact: LinkedContact): Patient {
        let patient = new Patient();
        patient.PatientResponse = linkedContact.Patient;
        patient.IndividualResponse = linkedContact.Individual;
        patient.ContactPoints = linkedContact.ContactPoints;
        patient.Addresses = linkedContact.Addresses;
        patient.Phones = linkedContact.PhoneNumbers;
        patient.Emails = linkedContact.Emails;
        return patient;
    }

    public static fromTransferablePatientInfoResponse(transferablePatientInfoResponse: TransferablePatientInfoResponse): Patient {
        let patient = new Patient();
        patient.IndividualResponse = new Individual();
        patient.PatientResponse = new ResponsePatient();
        patient.Phones = [];
        patient.Addresses = [];
        patient.Emails = [];
        patient.Relationships = [];
        patient.IndividualResponse.Contact_id = transferablePatientInfoResponse.ContactId;
        patient.IndividualResponse.FirstName = transferablePatientInfoResponse.FirstName;
        patient.IndividualResponse.LastName = transferablePatientInfoResponse.LastName;
        patient.IndividualResponse.DateOfBirth = transferablePatientInfoResponse.DateOfBirth;
        patient.IndividualResponse.ContactFacetId = GuidHelper.AsGuid(transferablePatientInfoResponse.ContactFacetId);

        return patient;
    }

    public static FromResponseData(data: LoadPatientModel): Patient {
        let obj = new Patient();
        obj.PatientResponse = data.Patient;
        obj.Emails = data.Emails?.mutate(p => p.renderId = sessionStore.newId().toString()) ?? [];
        obj.Phones = data.PhoneNumbers?.mutate(p => p.renderId = sessionStore.newId().toString()) ?? [];
        obj.Addresses = data.Addresses;
        obj.IndividualResponse = data.Individual;
        obj.ContactPoints = data.ContactPoints;
        obj.UpcomingAppointment = data.UpcomingAppointment;
        obj.ClinicalWaitingLists = data.ClinicalWaitingLists;
        obj.RelatedRecalls = data.RelatedRecalls;
        obj.HealthCards = data.HealthCards;
        obj.OtherPatientIdentifiers = data.OtherPatientIdentifiers;
        obj.PreferredPharmacy = new PharmacyProxy({
            customPharmacy: { ...data.PreferredPharmacy, Fax: data.PreferredPharmacy?.Fax?.replace(/\D/g, "") || "" },
            directoryPharmacyId: data.Patient.DirectoryPharmacyId
        });
        obj.InsuranceCompany = data.InsuranceCompany;
        obj.IntervenorsOnFile = data.IntervenorsOnFile != null ? data.IntervenorsOnFile.map(x => new PatientRefIntervenorWithDetailsModel(x)) : null;
        obj.Relationships = (data.Relationships ?? []).mutate(relationship => {
            relationship.Emails?.mutate(p => p.renderId = sessionStore.newId().toString());
            relationship.PhoneNumbers?.mutate(p => p.renderId = sessionStore.newId().toString());
            relationship.ReversePermissionFlags?.toString();
        });
        obj.ProfilePicture = data.ProfilePicture;
        obj.InactivableResponse = data.Inactivable;
        obj.ExternalIdentifiers = data.ExternalIdentifiers?.map(p => new PatientExternalIdentifier(p)) ?? [];

        obj.ConsentProgramHistory = data.ConsentProgramHistory;

        obj.possiblePatientWarning = this.getPossibleWarning(data);
        obj.IsReadOnly = data.Individual.IsReadOnly;
        obj.PatientFileAllowlist = data.PatientFileAllowlist ?? {
            MemberIds: [],
            AllowlistType: AllowlistType.PatientFileAllowlist,
            PatientContactFacetId: data.Patient.ContactFacet_id,
            IsActive: false
        };
        obj.ExternalPatientSubscriptionInfo = data.ExternalPatientSubscriptionInfo;
        return obj;
    }

    public ToPatientDemographicsModel(): IPatientDemographicsModel {
        let motherLinkedContact: LinkedContact = null;
        let fatherLinkedContact: LinkedContact = null;

        let motherRelationLookupId = cacheStore.FindLookupByLookupCode("RLMother").LookupID;
        let fatherRelationLookupId = cacheStore.FindLookupByLookupCode("RLFather").LookupID;
        if (this.Relationships != null && this.Relationships.length > 0) {
            let mother = this.Relationships.filter(l => l.RelationLookupId == motherRelationLookupId);
            if (mother != null && mother.length > 0)
                motherLinkedContact = mother[0];

            let father = this.Relationships.filter(l => l.RelationLookupId == fatherRelationLookupId);
            if (father != null && father.length > 0)
                fatherLinkedContact = father[0];
        }

        let obj = {
            AvailablePrefixLookupIds: cacheStore.GetPrefixIndividualLookups(),
            SelectedPrefixLookupId: this.IndividualResponse.PrefixLookupId ? this.IndividualResponse.PrefixLookupId : "",
            FirstName: this.IndividualResponse.FirstName,
            LastName: this.IndividualResponse.LastName,
            PreferredName: this.IndividualResponse.PreferredName,
            GenderIdentity: this.IndividualResponse.GenderIdentity,
            MiddleName: this.IndividualResponse.MiddleName,
            AvailableSuffixLookupIds: cacheStore.GetSuffixIndividualLookups(),
            SelectedSuffixLookupId: this.IndividualResponse.SuffixLookupId ? this.IndividualResponse.SuffixLookupId : "",
            PreferredPronouns: this.IndividualResponse.PreferredPronouns,
            SpecifiedPreferredPronouns: this.IndividualResponse.SpecifiedPreferredPronouns,
            MothersLastName: this.IndividualResponse.MotherLastName,
            MothersFirstName: this.IndividualResponse.MotherFirstName,
            FathersLastName: this.IndividualResponse.FatherLastName,
            FathersFirstName: this.IndividualResponse.FatherFirstName,
            MotherContactId: motherLinkedContact?.Individual?.Contact_id,
            MotherContactFacetType: motherLinkedContact?.Individual?.ContactFacetType,
            FatherContactId: fatherLinkedContact?.Individual?.Contact_id,
            FatherContactFacetType: fatherLinkedContact?.Individual?.ContactFacetType,
            SocialInsuranceNumber: this.PatientResponse.SocialNumber,
            AvailableGenderLookupIds: cacheStore.GetGenderLookups(),
            SelectedGenderLookupId: this.IndividualResponse.GenderLookupId ? this.IndividualResponse.GenderLookupId : "",
            SelectedEthnicityKeys: this.IndividualResponse.EthnicityKeys || Immutable.List<Ethnicities>(),
            DateOfBirth: this.IndividualResponse.DateOfBirth && new DateOnly(this.IndividualResponse.DateOfBirth.toString()).toJSDate(),
            AvailableLegalStatusLookupIds: cacheStore.GetMaritalStatusLookups(),
            SelectedLegalStatusLookupId: this.IndividualResponse.MaritalStatusLookupId ? this.IndividualResponse.MaritalStatusLookupId : "",
            AvailableOfficialLanguageLookupIds: cacheStore.GetLanguagesLookup(),
            SelectedOfficialLanguageLookupId: this.IndividualResponse.PreferredOfficialLanguageLookupId ? this.IndividualResponse.PreferredOfficialLanguageLookupId : "",
            AvailableSpokenLanguageLookupIds: cacheStore.GetLanguagesLookup(),
            SelectedSpokenLanguageLookupId: this.IndividualResponse.PreferredSpokenLanguageLookupId ? this.IndividualResponse.PreferredSpokenLanguageLookupId : "",
            AvailablePatientStatusLookupIds: cacheStore.GetPatientStatusLookups(),
            SelectedPatientStatusLookupId: this.PatientResponse.PatientStatusLookupId ? this.PatientResponse.PatientStatusLookupId : cacheStore.FindLookupByLookupCode("ACTIVE", "PATIENTSTATUS").LookupID,
            PatientStatusDate: this.PatientResponse.PatientStatusDate,
            Profession: this.PatientResponse.Profession,
            Employer: this.PatientResponse.Employer,
            Notes: this.PatientResponse.Notes,
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            Religion: cacheStore.GetReligions().filter(b => b.Code === this.IndividualResponse.Religion)[0] != null ? new ReligionCache(cacheStore.GetReligions().filter(b => b.Code === this.IndividualResponse.Religion)[0]) : null,
            ReligionCode: this.IndividualResponse.Religion,
            Religions: cacheStore.GetReligions().map(h => new ReligionCache(h))
        };

        let model: IPatientDemographicsModel = Immutable.fromJS(obj);
        return model;
    }

    public ToPatientIdentifiersModel(): IPatientIdentifiersModel {
        let healthCards: PatientIdentifier[] = [];
        let otherIds: PatientIdentifier[] = [];

        if (this.HealthCards != null) {
            this.HealthCards.forEach(x => {
                healthCards.push(x);

                if (x.ProvinceLookupId) {
                    x.CountryLookupId = cacheStore.GetCountryForRegion(x.ProvinceLookupId).LookupID;
                    x.AvailableCountryLookups = cacheStore.GetCountryLookups();
                    x.AvailableCountryRegionLookups = cacheStore.GetCountryRegionLookups(cacheStore.GetCountryForRegion(x.ProvinceLookupId));
                }
            });
        }

        if (this.OtherPatientIdentifiers != null) {
            this.OtherPatientIdentifiers.forEach(x => {
                otherIds.push(x);
            });
        }

        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            Identifier01: this.PatientResponse.Identifier01,
            Identifier02: this.PatientResponse.Identifier02,
            Identifier03: this.PatientResponse.Identifier03,
            HealthCards: Immutable.List<PatientIdentifier>(healthCards),
            OtherIdentifiers: Immutable.List<PatientIdentifier>(otherIds),
            AvailableExpiryYears: HealthCardHelper.getExpiryYears(),
            AvailableExpiryMonths: HealthCardHelper.getExpiryMonths()
        };

        let model: IPatientIdentifiersModel = Immutable.fromJS(obj);
        model = model.set("Representative", this.GetRepresentative());

        return model;
    }

    public ToPatientContactsModel(): IPatientContactsModel {
        const representatives: LinkedContact[] = this.Relationships?.filter(x => x.IsRepresentative) ?? [];
        const emergencyContacts: LinkedContact[] = this.Relationships?.filter(x => x.IsEmergencyContact) ?? [];

        for (let address of this.Addresses || []) {
            address.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
            address.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
            address.AvailableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetLookupById(address.CountryLookupId));
        }

        for (let phone of this.Phones || []) {
            phone.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
            phone.AvailablePhoneTypeLookupIds = cacheStore.GetPhoneContactPointTypeLookupTuples();
            phone.renderId = phone.renderId ?? sessionStore.newId().toString();
            if (phone.PhoneTypeLookupId === cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID)
                phone.isSelectedForNotification = phone.NotificationsEnabled;
        }

        for (let email of this.Emails || []) {
            email.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
            email.renderId = email.renderId ?? sessionStore.newId().toString();
            email.isSelectedForNotification = email.NotificationsEnabled;
        }

        for (let rep of representatives) {
            for (let phone of rep.PhoneNumbers || []) {
                phone.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
                phone.AvailablePhoneTypeLookupIds = cacheStore.GetPhoneContactPointTypeLookupTuples();
                phone.renderId = phone.renderId ?? sessionStore.newId().toString();
            }

            for (let email of rep.Emails || []) {
                email.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
                email.renderId = email.renderId ?? sessionStore.newId().toString();
            }
        }

        for (let contact of emergencyContacts) {
            for (let phone of contact.PhoneNumbers || []) {
                phone.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
                phone.AvailablePhoneTypeLookupIds = cacheStore.GetPhoneContactPointTypeLookupTuples();
                phone.renderId = phone.renderId ?? sessionStore.newId().toString();
            }

            for (let email of contact.Emails || []) {
                email.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
                email.renderId = email.renderId ?? sessionStore.newId().toString();
            }
        }

        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            PhoneNumbers: Immutable.List<PhoneNumberDeprecated>(this.Phones),
            ContactPoints: Immutable.List<ContactPoint>(this.ContactPoints),
            Addresses: Immutable.List<MyleModelsCommonAddress>(this.Addresses),
            Emails: Immutable.List<Email>(this.Emails),
            DoNotEmail: this.IndividualResponse.DoNotEmail,
            Representatives: Immutable.List(representatives),
            EmergencyContacts: Immutable.List(emergencyContacts)
        };

        let model: IPatientContactsModel = Immutable.fromJS(obj);
        model.set("Representatives", representatives.toImmutable());
        model.set("EmergencyContacts", emergencyContacts.toImmutable());
        return model;
    }

    public ToPatientOtherInformationModel(): IPatientOtherInformationModel {
        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            PreferredPharmacy: this.PreferredPharmacy,
            SelectedInsuranceDetailId: this.InsuranceCompany ? this.InsuranceCompany.Id : "",
            InsuranceCompanyName: this.InsuranceCompany ? this.InsuranceCompany.Provider : "",
            InsurancePolicyNo: this.InsuranceCompany ? this.InsuranceCompany.FileNumber : "",
            ConsentProgramPopup: false,
            ConsentPrograms: [],
            SelectedConsentProgram: null,
            SelectedConsentProgramId: "",
            SelectedConsentProgramDocGiven: false,
            SelectedConsentProgramHasConsented: false,
            SelectedConsentProgramHasRefused: false,
            ConsentProgramDialog: false,
            ConsentProgramHistory: Immutable.List(this.ConsentProgramHistory)
        };

        return Immutable.fromJS(obj);
    }

    public ToPatientCliniciansModel(): IPatientCliniciansModel {
        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id
        };

        let model: IPatientCliniciansModel = Immutable.fromJS(obj);

        return model
            .set("IntervenorsOnFile", this.IntervenorsOnFile.toImmutable())
            .set("PatientFileAllowlist", this.PatientFileAllowlist ?? {
                AllowlistType: AllowlistType.PatientFileAllowlist,
                PatientContactFacetId: this.PatientResponse.ContactFacet_id,
                MemberIds: [],
                IsActive: false
            });
    }

    public ToPatientRelationshipsModel(): IPatientRelationshipsModel {
        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            Relationships: Immutable.List(this.Relationships),
            AvailableRelationshipTypeLookupIds: Immutable.List(cacheStore.GetRelationshipLookupsTuples(this.Gender())),
            ShowRelationshipPopup: false,
            ShowRelationshipExternalPopup: false,
            DeleteRelationshipIds: Immutable.List([]),
            IsRepresentative: false,
            SelectedRelationshipTypeLookupString: "RLMother",
            RepresentativeType: "MOP",
            SelectedRelationshipPerson: null,
            SearchString: ""
        };

        return Immutable.fromJS(obj);
    }

    public ToPatientEnrollmentModel(): IPatientEnrollmentModel {
        let validDispensators = cacheStore.GetClinicianDoctors(p => RAMQInfo.IsValidDoctorLicence(p));
        if (cacheStore.ClinicSettings.EnableIpsEnrolmentForRamq)
            validDispensators.push(...cacheStore.GetIPS().filter(p => RAMQInfo.ValidateIPSRamqNumber(p.RamqNumber)));

        let obj = {
            PatientContactId: this.IndividualResponse.Contact_id,
            PatientContactFacetId: this.PatientResponse.ContactFacet_id,
            AvailableDispensateur: validDispensators.toImmutable(),
            AvailableResidents: Immutable.List(cacheStore.GetClinicianResidents()),
            AvailableIPS: Immutable.List(cacheStore.GetIPS()),
            AvailableRepType: Immutable.List(cacheStore.GetRAMQTypeRepresentant()),
            AvailableLieuSuivi: Immutable.List(cacheStore.SiteCache),
            AvailableTerminations: Immutable.List(cacheStore.GetRAMQTerminationTypes()),
            EnrollmentEffectiveDate: new Date()
        };

        let model: IPatientEnrollmentModel = Immutable.fromJS(obj);

        model = model.set("RAMQInscription", this.RAMQInscription);

        let rep = this.GetRepresentative();
        if (rep != null) {
            model = model
                .set("RepFirstName", LinkedContact.GetFirstName(rep))
                .set("RepLastName", LinkedContact.GetLastName(rep))
                .set("RepNam", LinkedContact.GetNAM(rep))
                .set("SelectedRepTypeId", rep.RepresentativeType);
        }

        return model;
    }

    public GetFormattedDateOfBirth(): string {
        return (this.IndividualResponse?.DateOfBirth)
            ? new DateOnly(this.IndividualResponse?.DateOfBirth).toString()
            : "";
    }

    public GetFormattedName(): string {
        return NameHelper.formatFullName(
            this.IndividualResponse.FirstName,
            this.IndividualResponse.LastName,
            this.IndividualResponse.PreferredName
        );
    }

    public GetFormattedNameWithPrefix(): string {
        return NameHelper.formatFullNameWithPrefixForHasName(this.IndividualResponse);
    }

    public GetFormattedGender(): string {
        let gender = "";
        if (this.IndividualResponse && this.IndividualResponse.GenderLookupId != null)
            gender = cacheStore.GetLocalizedValueByLookupId(this.IndividualResponse.GenderLookupId);

        return gender;
    }

    public GetFormattedPreferredPronouns(): string {
        if (this.IndividualResponse?.SpecifiedPreferredPronouns != null)
            return this.IndividualResponse?.SpecifiedPreferredPronouns;

        if (this.IndividualResponse?.PreferredPronouns != null && this.IndividualResponse?.PreferredPronouns != PreferredPronouns.Unknown)
            return cacheStore.GetString(`Enums_PreferredPronouns_${PreferredPronouns[this.IndividualResponse.PreferredPronouns]}`);

        return "";
    }

    public Gender(): Gender {
        return MyleHelper.genderLookupToEnum(this.IndividualResponse.GenderLookupId);
    }

    public GetRepresentative(): LinkedContact {
        if (this.Relationships == null)
            return null;

        let repLink = this.Relationships.filter(x => x.IsRepresentative);
        let repLinkWithNAM = repLink.filter(x => !IsNullOrWhiteSpace(LinkedContact.GetNAM(x)));
        if (repLinkWithNAM.length > 0)
            return repLinkWithNAM[0];
        if (repLink.length > 0)
            return repLink[0];
        return null;
    }

    public GetRepresentativeNAM(): string {
        let rep = this.GetRepresentative();
        return rep != null
            ? LinkedContact.GetNAM(rep)
            : "";
    }

    /**
     * Get a formatted Quebec health card number for display.
     * @param allowRepresentative Whether to default to the patient's representative's NAM, if applicable.
     */
    public GetFormattedNAM(allowRepresentative: boolean = true): string {
        if (this.IndividualResponse && !IsNullOrWhiteSpace(this.IndividualResponse.NAM))
            return this.IndividualResponse.NAM;

        if (this.HealthCards && this.HealthCards.length > 0 && !IsNullOrWhiteSpace(this.HealthCards[0].Number))
            return this.HealthCards[0].Number;

        if (allowRepresentative) {
            const repNAM = this.GetRepresentativeNAM();
            if (!IsNullOrWhiteSpace(repNAM))
                return `${repNAM} (${cacheStore.GetString("RepresentativeLabel")})`;
        }

        return "";
    }

    public GetAttendingDoctorOrSpecialistOnfileIntervenor(excludeEmployeeId: Guid = null): PatientRefIntervenorWithDetailsModel | null {
        return this.GetAttendingDoctorOnFile(excludeEmployeeId) ?? this.GetOtherDoctorOnFile(excludeEmployeeId);
    }

    public GetAttendingDoctor(): EmployeeLight | null {
        if (this.RAMQInfo) {
            let ramqProvider = this.RAMQInfo.getRamqProvider();
            if (ramqProvider) {
                return ramqProvider;
            }
        }

        const attendingDoctor = this.GetAttendingDoctorOnFile();

        if (attendingDoctor) {
            const employee = attendingDoctor.Employee;
            const contactFacetId = employee?.Model?.ContactFacet_id;
            const nameWithPrefix = employee?.nameWithPrefix();
            const licenseNumber = employee?.getLicenseDtos()?.firstOrDefault()?.LicenseNumber;

            return new EmployeeLight(contactFacetId, nameWithPrefix, licenseNumber);
        }

        return null;
    }

    public GetAttendingDoctorOnFile(excludeEmployeeId: Guid = null): PatientRefIntervenorWithDetailsModel | null {
        if (!this.IntervenorsOnFile) return null;

        const attendingDoctor = this.IntervenorsOnFile.find(intervenor =>
            intervenor.Model.Intervenor.IsPrimary &&
            intervenor.getTitleAndSpecialtyModel()?.licenceOrganisationSpecialisationModel?.SpecialisationCode === "0" &&
            EmployeeTitleHelper.isDoctor(intervenor.getTitleAndSpecialtyModel()?.licenceOrganisationRuleModel?.Title) &&
            (!excludeEmployeeId || intervenor?.Employee?.Model?.ContactFacet_id != excludeEmployeeId)
        );

        return attendingDoctor || null;
    }

    public GetOtherDoctorOnFile(excludeEmployeeId: Guid = null): PatientRefIntervenorWithDetailsModel | null {
        if (!this.IntervenorsOnFile) return null;

        const otherDoctor = this.IntervenorsOnFile.find(intervenor =>
            intervenor.Model?.Intervenor?.IsPrimary &&
            EmployeeTitleHelper.isDoctor(intervenor.getTitleAndSpecialtyModel()?.licenceOrganisationRuleModel?.Title) &&
            (!excludeEmployeeId || intervenor?.Employee?.Model?.ContactFacet_id != excludeEmployeeId)
        );

        return otherDoctor || null;
    }

    public GetResidentIntervenorOnFile(): PatientRefIntervenorWithDetailsModel | null {
        if (!this.IntervenorsOnFile) return null;

        const residentIntervenor = this.IntervenorsOnFile.find(intervenor =>
            intervenor.Model?.Intervenor?.IsPrimary &&
            intervenor.getTitleAndSpecialtyModel()?.licenceOrganisationSpecialisationModel?.SpecialisationCode === "0" &&
            intervenor.getTitleAndSpecialtyModel()?.licenceOrganisationRuleModel?.Title === EmployeeTitle.Resident
        );

        return residentIntervenor || null;
    }

    public GetIpsIntervenorOnFile(): PatientRefIntervenorWithDetailsModel {
        if (!this.IntervenorsOnFile)
            return null;

        const primarySpecializedNurse = this.IntervenorsOnFile.filter(p => p.Model?.Intervenor?.IsPrimary && p.Model?.LicenceOrganisationRule?.Title === EmployeeTitle.NursePractitioner).firstOrDefault();
        const rule = primarySpecializedNurse?.Model?.LicenceOrganisationRule;
        if (!rule)
            return null;

        const isIpsIntervenor = (rule.CountryCode === "CA" &&
            ((rule.StateCode === "QC" && rule.OrganisationCode === "OIIQ") ||
                (rule.StateCode === "BC" && rule.OrganisationCode === "BCCNM"))
        );
        return isIpsIntervenor ? primarySpecializedNurse : null;
    }

    public HasRAMQDoctor(): boolean {
        return this.RAMQInfo?.HasRamqDoctorStatus() == true;
    }

    public HasDoctor(): boolean {
        return this.HasRAMQDoctor()
            || this.GetAttendingDoctorOrSpecialistOnfileIntervenor() != null
            || this.GetResidentIntervenorOnFile() != null;
    }

    public HasIntervenorOnFile(): boolean {
        return this.IntervenorsOnFile?.filter(p => p.Model?.Intervenor?.IsPrimary)?.any() == true;
    }

    public GetAssignedDoctorStatus(): CommonStatus {
        if (this.RAMQInfo == null
            || this.RAMQInfo.Errors?.any()
            || this.RAMQInfo?.Result == null
            || this.RAMQInfo?.Result == RamqStatus.Undefined
            || this.RAMQInfo?.Result == RamqStatus.CrashWhileLoadingCachedInscriptionData
            || this.RAMQInfo?.Result == RamqStatus.NoInformation_UnableToSearchRamq
            || this.RAMQInfo?.Result == RamqStatus.NoNAM
            || this.RAMQInfo?.Result == RamqStatus.InvalidNAM
            || this.RAMQInfo?.Result == RamqStatus.OtherError
            || this.RAMQInfo?.Result == RamqStatus.InvalidSearchParameters
            || this.RAMQInfo?.Result == RamqStatus.NoInscriptionFoundInCache
            || this.RAMQInfo?.Result == RamqStatus.RamqIsDisabledForEnvironnement)
            return CommonStatus.NoInfo;

        if (this.RAMQInscription && (this.RAMQInfo.Result == RamqStatus.SubscribedInClinic || this.RAMQInfo.Result == RamqStatus.SubscribedWithoutVisit)) {
            const codeLieu = this.RAMQInscription.Lieux.firstOrDefault()?.CodeLieu;
            const isWithCurrentClinic = cacheStore.SiteCache.any(s => compareNumbers(s.Model.ClinicalCode, codeLieu));

            const activeProfessional = RAMQInfo.activeProfessional(this.RAMQInscription.DoctorClass, this.RAMQInscription.DoctorLicence);

            if (isWithCurrentClinic && activeProfessional)
                return CommonStatus.Covered;

            // Synchronized from Ramq database but doctor has no account in clinic or patient is not in clinic
            if (isWithCurrentClinic || activeProfessional) {
                return CommonStatus.Question;
            }
        }

        const gl6GroupLocationCode = this.RAMQInfo?.RamqGl6Info?.GmfRegistration.Code;
        if (!IsNullOrWhiteSpace(gl6GroupLocationCode)) {
            const isWithCurrentClinicGroup = cacheStore.SiteCache.any(s => s.Model.ClinicalCode == gl6GroupLocationCode);
            if (isWithCurrentClinicGroup)
                return CommonStatus.CoveredWithGroup;

            return CommonStatus.CoveredWithAnotherGroup;
        }

        const gl6DoctorLocationCode = this.RAMQInfo?.RamqGl6Info?.Doctor?.Location;
        if (!IsNullOrWhiteSpace(gl6DoctorLocationCode)) {
            const isWithCurrentClinic = cacheStore.SiteCache.any(s => s.Model.ClinicalCode == gl6DoctorLocationCode);
            const familyDoctorHasActiveAccount = RAMQInfo.ResolveActiveClinician(this.RAMQInfo.Extract5DigitLicenceNumber());

            if (isWithCurrentClinic && familyDoctorHasActiveAccount)
                return CommonStatus.Covered;

            if (!isWithCurrentClinic && !familyDoctorHasActiveAccount)
                return CommonStatus.CoveredOutsideClinic;

            return CommonStatus.Question;
        }

        // if nothing was hit, return not covered
        return CommonStatus.NotCovered;
    }

    public GetFormattedRamqDoctorOrStatus(): string {
        let hasRamqInterface = sessionStore.clientConfiguration.HasMedfarRAMQ == null
            || sessionStore.clientConfiguration.HasMedfarRAMQ
            || cacheStore.ClinicSettings.EnableRamqGL6Service;

        if (!hasRamqInterface)
            return "";

        if (hasRamqInterface && this.RAMQInfo == null)
            return cacheStore.GetString("LoadingLabel");

        const status = this.RAMQInfo.getFormattedRamqInfoByStatus();
        if (IsNullOrWhiteSpace(status))
            return cacheStore.GetString("NoInfoRamqLabel");

        return status;
    }

    public GetFormattedRamqCV(): string {
        return this.RAMQInfo?.Result == RamqStatus.SubscribedInClinic
            ? this.RAMQInfo.getFormattedRamqCV()
            : "";
    }

    public GetFormattedPhysicianName(): string {
        return this.GetFormattedRamqDoctorOrStatus()
            || this.GetFormattedIntervenorOnFile();
    }

    public GetPhysicianNameByProvince(): string {
        const activeSite = sessionStore.getCurrentEmployee().getActiveSite();

        const isSiteInBritishColumbia = activeSite.isPreferredSiteAddressInProvince(CanadaState.BritishColumbia);

        if (isSiteInBritishColumbia)
            return this.GetFormattedIntervenorOnFile();

        return this.GetFormattedRamqDoctorOrStatus();
    }

    public GetFormattedIntervenorOnFile(): string {
        const familyDoctor: PatientRefIntervenorWithDetailsModel = this.GetAttendingDoctorOrSpecialistOnfileIntervenor();
        const residentIntervenor: PatientRefIntervenorWithDetailsModel = this.GetResidentIntervenorOnFile();
        const ipsIntervenor: PatientRefIntervenorWithDetailsModel = this.GetIpsIntervenorOnFile();

        const familyDoctorName = familyDoctor?.Model?.Intervenor?.IsPrimary ? familyDoctor.GetName() : null;
        const residentIntervenorName = residentIntervenor?.Model?.Intervenor?.IsPrimary ? `R: ${residentIntervenor.GetName()}` : null;
        const ipsIntervenorName = ipsIntervenor?.Model?.Intervenor?.IsPrimary ? `${cacheStore.GetString("NurseSpecializedLabel")}: ${ipsIntervenor.GetName()}` : null;

        return ipsIntervenorName
            ?? residentIntervenorName
            ?? familyDoctorName
            ?? this.IntervenorsOnFile?.filter(p => p.Model?.Intervenor?.IsPrimary).firstOrDefault()?.GetName()
            ?? "";
    }

    public GetRequestingStaffOnFile(): PatientRefIntervenorWithDetailsModel {
        const familyDoctor: PatientRefIntervenorWithDetailsModel = this.GetAttendingDoctorOrSpecialistOnfileIntervenor();
        const residentIntervenor: PatientRefIntervenorWithDetailsModel = this.GetResidentIntervenorOnFile();
        const ipsIntervenor: PatientRefIntervenorWithDetailsModel = this.GetIpsIntervenorOnFile();

        const familyDoctorName = familyDoctor?.Model?.Intervenor?.IsPrimary ? familyDoctor : null;
        const residentIntervenorName = residentIntervenor?.Model?.Intervenor?.IsPrimary ? residentIntervenor : null;
        const ipsIntervenorName = ipsIntervenor?.Model?.Intervenor?.IsPrimary ? ipsIntervenor : null;

        return ipsIntervenorName
            ?? residentIntervenorName
            ?? familyDoctorName
            ?? this.IntervenorsOnFile?.filter(p => p.Model?.Intervenor?.IsPrimary).firstOrDefault();
    }

    public GetFormattedAge(now: Date = null): string {
        if (this.IndividualResponse.DateOfBirth)
            return MyleHelper.GetFormattedAge(this.IndividualResponse.DateOfBirth, now);

        return "";
    }

    public getAge(): number {
        return this.calculateAge(moment().toDate());
    }

    public getAgeFromEncounterDate(encounterDate: Date): number {
        if (!this.IndividualResponse?.DateOfBirth)
            return null;

        return this.calculateAge(moment(encounterDate).toDate());
    }

    private calculateAge(date: Date): number {
        const birthdate = this.IndividualResponse?.DateOfBirth;

        let age = date.getFullYear() - moment(birthdate).toDate().getFullYear();
        let month = date.getMonth() - moment(birthdate).toDate().getMonth();

        if (month < 0 || month === 0 && date.getDate() < moment(birthdate).toDate().getDate()) {
            age--;
        }

        return age;
    }

    public IsRegisteredToPortal(): boolean {
        let syncState = this.IndividualResponse && this.IndividualResponse.PortalSyncState;
        return syncState != null
            && (
                syncState == PortalRegistrationState.RegistrationComplete
                || syncState == PortalRegistrationState.RegistrationPending
                || syncState == PortalRegistrationState.ChildAccount
            );
    }

    public GetNextAppointmentFormatted(): string {
        let text = "Ø " + cacheStore.GetString("FutureAppointmentsLabel");
        if (this.UpcomingAppointment != null) {
            let today = this.UpcomingAppointment.HasAppointmentToday
                ? cacheStore.GetString("TodayLabel") + "."
                : "";

            if (this.UpcomingAppointment.Subject == null)
                this.UpcomingAppointment.Subject = "";

            let nextAppDateDisplay = "";
            if (this.UpcomingAppointment.DateTime != null) {
                today = today == "" ? "" : today.substring(0, 3) + "+";
                nextAppDateDisplay = moment(this.UpcomingAppointment.DateTime).format("YYYY-MM-DD HH:mm A");
            }

            const upcomingAppointmentDescription = cacheStore.GetAppointmentDescription(this.UpcomingAppointment.Subject, this.UpcomingAppointment.ServiceDefinitionId, this.UpcomingAppointment.ComboId);

            text = cacheStore.GetString("NextAppointmentDLabel") + " " + today + nextAppDateDisplay + " " + upcomingAppointmentDescription;
        }

        return text;
    }

    public GetIdentifiers(): string[] {
        if (!this.PatientResponse)
            return [];

        return [
            !IsNullOrWhiteSpace(this.PatientResponse.Identifier01) ? this.PatientResponse.Identifier01 : null,
            !IsNullOrWhiteSpace(this.PatientResponse.Identifier02) ? this.PatientResponse.Identifier02 : null,
            !IsNullOrWhiteSpace(this.PatientResponse.Identifier03) ? this.PatientResponse.Identifier03 : null
        ]
            .filter(p => p);
    }

    public GetFormattedIdentifiers(maxLength: number = 30): string {
        if (this.PatientResponse == null)
            return "";

        let result = this.PatientResponse.ChartNumber;
        let addSeparator = false;

        if (!IsNullOrWhiteSpace(this.PatientResponse.Identifier01)) {
            let identifierLength = this.PatientResponse.Identifier01.length;
            if (!IsNullOrWhiteSpace(this.PatientResponse.Identifier02) || !IsNullOrWhiteSpace(this.PatientResponse.Identifier03) || identifierLength > maxLength)
                identifierLength = maxLength;

            result = identifierLength < this.PatientResponse.Identifier01.length
                ? this.PatientResponse.Identifier01.substring(0, identifierLength) + "..."
                : this.PatientResponse.Identifier01;

            addSeparator = true;
        }

        if (!IsNullOrWhiteSpace(this.PatientResponse.Identifier02)) {
            let identifier02 = this.PatientResponse.Identifier02.length > maxLength
                ? this.PatientResponse.Identifier02.substring(0, maxLength) + "..."
                : this.PatientResponse.Identifier02;
            if (addSeparator) {
                result += " / ";
                result += identifier02;
            }
            else
                result = identifier02;

            addSeparator = true;
        }

        if (!IsNullOrWhiteSpace(this.PatientResponse.Identifier03)) {
            let identifier03 = this.PatientResponse.Identifier03.length > maxLength
                ? this.PatientResponse.Identifier03.substring(0, maxLength) + "..."
                : this.PatientResponse.Identifier03;

            if (addSeparator) {
                result += " / ";
                result += identifier03;
            }
            else
                result = identifier03;
        }

        return result ?? "";
    }

    public GetNAMExpirationDate(): Date {
        return this.HealthCards != null && this.HealthCards.length > 0
            ? this.HealthCards[0].ExpiryDate
            : null;
    }

    public GetPrimaryHealthCardCountryLookupId(): string {
        let firstHealthCard = this.HealthCards != null && this.HealthCards.length > 0
            ? this.HealthCards[0]
            : null;

        if (!firstHealthCard)
            return null;

        let lookupId = firstHealthCard.CountryLookupId;
        if (lookupId == null && lookupId == undefined) {
            let regionId = firstHealthCard.ProvinceLookupId;
            if (regionId != null) {
                let country = cacheStore.GetCountryForRegion(regionId);
                if (country != null)
                    lookupId = country.LookupID;
            }
        }

        return lookupId;
    }

    /**
     * Get the health card ID translation for the patient's main health card region.
     */
    public GetPrimaryHealthCardLabel(): string {
        // get primary health card
        let card = this.HealthCards && this.HealthCards.length > 0
            ? this.HealthCards[0]
            : null;

        // get label
        return cacheStore.GetHealthCardLabel(card && card.CountryLookupId, card && card.ProvinceLookupId);
    }

    public hasHealthCardInProvince(province: CanadaState): boolean {
        if (!province)
            return false;

        return this.HealthCards?.any(p => p.ProvinceLookupId === cacheStore.GetLookupIdForProvince(province));
    }

    public isPatientDSQSynced(): boolean {
        return this.IndividualResponse.NUI != null && this.IndividualResponse.NUI != "";
    }

    public getAllFullFormattedAddress(): string[] {
        const addressLength: number = this.Addresses.length;
        return this.Addresses.map((ad, index) => (index + 1) != addressLength
            ? MyleModelsCommonResponseAddress.GetFullAddress(ad) + " / "
            : MyleModelsCommonResponseAddress.GetFullAddress(ad)
        );
    }

    public getFullFormattedAddress(): string {
        let address = this.Addresses.firstPreferredOrDefault(x => x.IsPreferred);
        if (!address)
            return "";

        return MyleModelsCommonResponseAddress.GetFullAddress(address);
    }

    public GetPhoneRaw(position: number): string {
        if (this.Phones != null && position < this.Phones.length && this.Phones[position] != null)
            return this.Phones[position].Number;
    }

    public GetFormattedPhonesByPriorityWithPosition(index: number, format: string = "+{0} ({1}) {2} {3} {4}", formatExtension: string = " Ext.{Ext}"): string {
        let phones = this.GetPhonesByPriority();
        if (phones.length > index)
            return phones[index].format(format, formatExtension);
        return "";
    }

    public GetPhonesByPriority(): PhoneNumber[] {
        return PhoneNumber.GetPhonesByPriority(this.Phones);
    }

    public IsApexSynched(): boolean {
        return cacheStore.IsApexInterfaceActive()
            && this.IndividualResponse.FileNumber != null
            && this.IndividualResponse.FileNumber.indexOf(cacheStore.ApexInterfaceCode) == 0;
    }

    public static IsApexSynchedError: string = "El paciente no tiene un Apex identificación";

    public GetApexId(): string {
        return this.IsApexSynched()
            ? this.IndividualResponse.FileNumber.replaceAll(cacheStore.ApexInterfaceCode, "")
            : null;
    }

    public GetVisibleExternalIdentifersForActiveSite(): PatientExternalIdentifier[] {
        return this.GetVisibleExternalIdentifersForSiteId(sessionStore.getCurrentEmployee().getActiveSite()?.Model.Id);
    }

    public GetVisibleExternalIdentifersForSiteId(sideId: string): PatientExternalIdentifier[] {
        return this.GetVisibleExternalIdentifers().filter(p => p.RelatedSites.contains(sideId)) ?? [];
    }

    // Get the identifiers that should be used for ExternalSearches
    public GetVisibleExternalIdentifers(): PatientExternalIdentifier[] {
        return this.ExternalIdentifiers
            ?.filter(p => p.IsVisible)
            ?? [];
    }

    public get hasNotifiableContactPoints(): boolean {
        const mobileId = cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID;
        const contactPoints = [
            cacheStore.getLookupByContactPointType(ContactPointType.Personal).LookupID,
            cacheStore.getLookupByContactPointType(ContactPointType.Work).LookupID
        ];

        return this.Emails?.any(p => contactPoints.contains(p.ContactPointTypeLookupId))
            || this.Phones?.any(p => p.PhoneTypeLookupId === mobileId && contactPoints.contains(p.ContactPointTypeLookupId));
    }

    public setOntarioPatientEnrollmentModel(model: OntarioPatientEnrollmentModel): void {
        this.OntarioPatientEnrollmentModel = model;
    }

    private static getPossibleWarning(data: LoadPatientModel): PossiblePatientWarning[] {
        let warnings: PossiblePatientWarning[] = [];

        if (!data.Individual.GenderLookupId)
            warnings.push(PossiblePatientWarning.SexAtBirthMissing);

        return warnings;
    }
}

export { Patient };
